export type ReplaceItemType = 'hotel' | 'flight' | 'ticket';

export type AdminReplaceParams = {
  orderId: string;
  stepId: string;
  shoppingCartId: string;
  itemType: ReplaceItemType;
  // The item's original price (see OrderReplaceService.prepareReplace) — carried through
  // so the confirm page can compute the price delta itself.
  oldPrice: number;
  // Rooms the failed hotel hold books — the replacement is searched, priced and held for the
  // same number (absent on sessions stored before this existed, i.e. 1).
  rooms?: number;
  // Rate the backend adds on top of a positive delta when the order has refund
  // protection (0 otherwise) — so the confirm page can show what will really be charged.
  refundProtectionSurchargeRate: number;
};

const STORAGE_KEY = 'admin-replace-session';

// An abandoned replace flow (admin navigates away / closes the tab before reaching the
// confirm page) would otherwise leave this key set forever — and the next NORMAL
// checkout's flight/room/ticket selection would misroute to the admin confirm page.
// Expire it well after any realistic replace takes (and past the 30-min flight-fare
// validity window the flow itself relies on).
const SESSION_TTL_MS = 60 * 60 * 1000;

// Carries the admin "replace this failed item" session through the normal
// search/selection pages — set once when OrderStepsPanel's Replace button starts the
// flow, then read by RoomSelectionBottomHeader/TicketSelectionBottomHeader/FlightDrawer
// to skip GA4/tracking and redirect to the admin confirm page instead of checkout.
//
// Deliberately storage-backed, NOT query params: confirmed live that the search ->
// hotel-details -> room-selection navigation chain only forwards eventId/eventLink/
// packageType in its own internal links (see RoomSelectionBottomHeader's own
// checkoutUrl construction) — any other query param we added got silently dropped
// partway through and the flow fell through to a real customer checkout. Storage
// survives navigation regardless of what each page's links carry forward.
type StorageLike = Pick<Storage, 'setItem' | 'getItem' | 'removeItem'>;

export function setAdminReplaceParams(
  storage: StorageLike,
  params: AdminReplaceParams
) {
  storage.setItem(STORAGE_KEY, JSON.stringify({ ...params, setAt: Date.now() }));
}

export function clearAdminReplaceParams(storage: StorageLike) {
  storage.removeItem(STORAGE_KEY);
}

export function readAdminReplaceParams(
  storage: StorageLike
): { isAdminReplace: false } | ({ isAdminReplace: true } & AdminReplaceParams) {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return { isAdminReplace: false };
    const parsed = JSON.parse(raw) as Partial<AdminReplaceParams> & { setAt?: number };
    if (!parsed.orderId || !parsed.stepId || !parsed.shoppingCartId || !parsed.itemType) {
      return { isAdminReplace: false };
    }
    if (!parsed.setAt || Date.now() - parsed.setAt > SESSION_TTL_MS) {
      storage.removeItem(STORAGE_KEY);
      return { isAdminReplace: false };
    }
    return { isAdminReplace: true, ...(parsed as AdminReplaceParams) };
  } catch {
    return { isAdminReplace: false };
  }
}
